import { useEffect } from "react";

/**
 * Scroll-reveal for direct children of a container (the homepage <main>).
 * - Skips elements already visible on attach (no flash above the fold).
 * - Skips the first child (hero) to protect LCP.
 * - Picks up lazy-loaded sections via MutationObserver.
 * - Safety timeout reveals anything the observer somehow missed.
 */
export const useScrollReveal = (ref: React.RefObject<HTMLElement | null>) => {
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add("reveal-in");
            io.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.12, rootMargin: "0px 0px -6% 0px" }
    );

    const observed = new WeakSet<Element>();

    const attach = () => {
      const kids = root.children;
      for (let i = 0; i < kids.length; i++) {
        const el = kids[i];
        if (observed.has(el)) continue;
        observed.add(el);
        // Never animate the hero (first child) — protects LCP.
        if (i === 0) continue;
        // Already in view on attach: show instantly, no flash.
        if (el.getBoundingClientRect().top < window.innerHeight * 0.92) continue;
        el.classList.add("reveal");
        io.observe(el);
        // Safety net: never leave content hidden.
        window.setTimeout(() => el.classList.add("reveal-in"), 5000);
      }
    };

    attach();
    const mo = new MutationObserver(attach);
    mo.observe(root, { childList: true, subtree: false });

    return () => {
      io.disconnect();
      mo.disconnect();
    };
  }, [ref]);
};
